import { MeasurementType } from "@speckle/shared/viewer/state";

// All four keys stay — MeasurementsAsExcel indexes this by the SDK's full enum — though Perpendicular
// and Point never appear: the Measure panel's chips don't offer either mode.
export const KIND_LABELS: Record<MeasurementType, string> = {
  [MeasurementType.POINTTOPOINT]: "Lines",
  [MeasurementType.PERPENDICULAR]: "Thicknesses",
  [MeasurementType.AREA]: "Areas",
  [MeasurementType.POINT]: "Points",
};

// Only the kinds the panel offers — a kind outside this list is never counted, even if it has items,
// since it could only mean stale or externally-authored data.
const KIND_ORDER = [MeasurementType.POINTTOPOINT, MeasurementType.AREA];

export interface KindCount {
  type: MeasurementType;
  label: string;
  count: number;
}

// One entry per kind present, in a fixed order, for the footer: the list itself keeps the order measured.
export const countsByKind = <T>(items: T[], typeOf: (item: T) => MeasurementType): KindCount[] =>
  KIND_ORDER.map((type) => ({
    type,
    label: KIND_LABELS[type],
    count: items.filter((item) => typeOf(item) === type).length,
  })).filter((entry) => entry.count > 0);

// "Areas" -> "2 areas" / "1 area" — the section header's own kind label, lowered and singularised
// for a count of one.
export const kindCount = (label: string, count: number): string => {
  const lower = label.toLowerCase();
  const word = count === 1 && lower.endsWith("s") ? lower.slice(0, -1) : lower;
  return `${count} ${word}`;
};

// Sums every item's own value into its kind, regardless of chain membership. No units here — the
// caller picks one unit to display the sum in, rather than inheriting whichever item summed in last.
export const totalsByKind = <T>(
  items: T[],
  typeOf: (item: T) => MeasurementType,
  valueOf: (item: T) => number
): Partial<Record<MeasurementType, number>> => {
  const totals: Partial<Record<MeasurementType, number>> = {};
  for (const item of items) {
    const type = typeOf(item);
    totals[type] = (totals[type] ?? 0) + valueOf(item);
  }
  return totals;
};
